import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { ReactComponent as Eye } from "../assets/images/icons/Eye.svg";
import { ReactComponent as Lock } from "../assets/images/icons/Lock.svg";
import { ReactComponent as Login } from "../assets/images/icons/Login.svg";
import { Button } from "./Button";
import AuthService from "../services/authService";
import { useTranslation } from "react-i18next";

export const AuthModal = ({ isOpen, close }) => {
  const { t } = useTranslation("common");
  const { login } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStatus, setForgotStatus] = useState("");

  const [formData, setFormData] = useState({
    fullName: "",
    company: "",
    email: "",
    password: "",
    confirmPassword: "",
    customerCode: "",
    website: "",
    languageId: "PT",
  });

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{6,}$/;
    return regex.test(password);
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const hasManyConsonants = (text) => {
    const normalized = text.toLowerCase().replace(/[^a-z]/g, "");
    if (normalized.length < 8) return false;

    const consonants = normalized.match(/[bcdfghjklmnpqrstvwxyz]/g)?.length || 0;
    return consonants / normalized.length > 0.82;
  };

  const looksLikeRandomString = (text) => {
    const compact = text.replace(/\s/g, "");
    if (compact.length < 10) return false;
    if (/^[A-Za-z]+$/.test(compact) && !/[aeiouAEIOU]/.test(compact)) return true;
    if (/([A-Za-z])\1{3,}/.test(compact)) return true;
    return false;
  };

  const isLikelySpamRegistration = () => {
    const fullName = formData.fullName.trim();
    const company = formData.company.trim();
    const customerCode = formData.customerCode.trim();

    if (formData.website.trim()) return true;
    if (!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/.test(fullName)) return true;
    if (hasManyConsonants(fullName) || looksLikeRandomString(fullName)) return true;
    if (company && looksLikeRandomString(company)) return true;
    if (customerCode && /[^A-Za-z0-9\-_/]/.test(customerCode)) return true;
    if (/^(test|demo|spam|qwerty|asdf|abcd)/i.test(fullName)) return true;

    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!isRegister) {
      try {
        await login(formData.email, formData.password);
        window.location.reload();
        close();
      } catch {
        setError("Credenciais invalidas.");
      }
    } else {
      if (!validatePassword(formData.password)) {
        setError("Password deve ter 6 caracteres, uma maiuscula, minuscula e simbolo.");
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError("Passwords nao coincidem.");
        setLoading(false);
        return;
      }

      if (isLikelySpamRegistration()) {
        // Silent block: emulate normal response and avoid exposing the filter.
        await wait(700);
        alert("Pedido enviado com sucesso!");
        close();
        setLoading(false);
        return;
      }

      try {
        await AuthService.requestAccess({
          FullName: formData.fullName.trim(),
          CompanyName: formData.company.trim(),
          Email: formData.email.trim(),
          CustomerCode: formData.customerCode.trim(),
          LanguageId: formData.languageId,
          Password: formData.password,
          ConfirmPassword: formData.confirmPassword,
        });

        alert("Pedido enviado com sucesso!");
        close();
      } catch (submitError) {
        setError("Pedido nao foi enviado.");
      }
    }

    setLoading(false);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setForgotStatus("");
    setLoading(true);

    try {
      await AuthService.forgotPassword(forgotEmail.trim());
    } catch {
      // Ignored: response is always generic to avoid disclosing whether the email is registered.
    }

    setForgotStatus(t("account.forgotPassword.success"));
    setLoading(false);
  };

  const backToLogin = () => {
    setShowForgotPassword(false);
    setForgotEmail("");
    setForgotStatus("");
    setError("");
  };

  return (
    <>
      <div className="auth-modal__dimmer" onClick={close}></div>
      <div className="auth-modal">
        <div className="auth-modal__container">
          <form
            onSubmit={showForgotPassword ? handleForgotPasswordSubmit : handleSubmit}
            className="auth-modal__form"
          >
            {showForgotPassword ? (
              <>
                <h4>{t("account.forgotPassword.title")}</h4>
                {error && <p className="auth-modal__error">{error}</p>}
                {forgotStatus ? (
                  <p className="auth-modal__success">{forgotStatus}</p>
                ) : (
                  <>
                    <p className="auth-modal__hint">
                      {t("account.forgotPassword.description")}
                    </p>
                    <label>{t("account.email")}</label>
                    <input
                      type="email"
                      placeholder={t("account.email")}
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                    <Button
                      text={
                        loading
                          ? t("account.forgotPassword.sendingMessage")
                          : t("account.forgotPassword.button")
                      }
                      classe="button--primary"
                      Icon={Lock}
                    />
                  </>
                )}
                <p className="auth-modal__switch">
                  <span onClick={backToLogin}>{t("account.forgotPassword.back")}</span>
                </p>
              </>
            ) : !isRegister ? (
              <>
                {error && <p className="auth-modal__error">{error}</p>}
                <label>{t("account.email")}</label>
                <input
                  type="email"
                  placeholder={t("account.email")}
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                />
                <label>{t("account.password")}</label>
                <div className="auth-modal__password">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("account.password")}
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    required
                  />
                  <Eye
                    className="auth-modal__eye"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </div>
                <p className="auth-modal__switch auth-modal__switch--right">
                  <span onClick={() => setShowForgotPassword(true)}>
                    {t("account.forgotPassword.link")}
                  </span>
                </p>
                <Button
                  text={loading ? "..." : t("account.login")}
                  classe="button--primary"
                  Icon={Login}
                />
                <p className="auth-modal__switch">
                  {t("account.resquestAccess.first")}
                  <span onClick={() => setIsRegister(true)}>
                    {t("account.resquestAccess.link")}
                  </span>
                </p>
              </>
            ) : (
              <>
                <h4>{t("registration.title")}</h4>
                {error && <p className="auth-modal__error">{error}</p>}
                <label>{t("registration.labels.name")}</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleChange("fullName", e.target.value)}
                  required
                />
                <label>{t("registration.labels.company")}</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange("company", e.target.value)}
                />
                <label>{t("registration.labels.email")}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                />
                <label>{t("registration.labels.language")}</label>
                <select
                  value={formData.languageId}
                  onChange={(e) => handleChange("languageId", e.target.value)}
                >
                  <option value="PT">🇵🇹 Português</option>
                  <option value="EN">🇬🇧 English</option>
                  <option value="ES">🇪🇸 Español</option>
                  <option value="FR">🇫🇷 Français</option>
                </select>
                <label>{t("registration.labels.password")}</label>
                <div className="auth-modal__password">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    required
                  />
                  <Eye
                    className="auth-modal__eye"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </div>
                <label>{t("registration.labels.confirmPassword")}</label>
                <div className="auth-modal__password">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange("confirmPassword", e.target.value)}
                    required
                  />
                  <Eye
                    className="auth-modal__eye"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </div>
                <label>{t("registration.labels.customerCode")}</label>
                <input
                  type="text"
                  value={formData.customerCode}
                  onChange={(e) => handleChange("customerCode", e.target.value)}
                />
                <input
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  value={formData.website}
                  onChange={(e) => handleChange("website", e.target.value)}
                  style={{
                    position: "absolute",
                    left: "-9999px",
                    width: "1px",
                    height: "1px",
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                  aria-hidden="true"
                />
                <Button
                  text={
                    loading
                      ? t("registration.buttons.sendingMessage")
                      : t("registration.buttons.register")
                  }
                  classe="button--primary"
                  Icon={Lock}
                />
                <p className="auth-modal__switch">
                  {t("registration.loginToAccount.first")}
                  <span onClick={() => setIsRegister(false)}>
                    {t("registration.loginToAccount.link")}
                  </span>
                </p>
              </>
            )}
          </form>
        </div>
      </div>
    </>
  );
};
